import type { ShapeKind } from '../doc/schema';
import { IconArrow, IconEllipse, IconEraser, IconLine, IconMarker, IconPen, IconRect } from '../ui/icons';
import { ColorPicker, SizePicker } from '../ui/ColorPicker';
import { usePane } from './context';
import { setToolSettings, useToolSettings, type PenMode } from './toolSettings';

const SHAPES: { kind: ShapeKind; label: string; icon: () => React.JSX.Element }[] = [
  { kind: 'rect', label: 'Rectangle', icon: IconRect },
  { kind: 'ellipse', label: 'Ellipse', icon: IconEllipse },
  { kind: 'line', label: 'Line', icon: IconLine },
  { kind: 'arrow', label: 'Arrow', icon: IconArrow },
];

const PENS: { mode: PenMode; label: string; icon: () => React.JSX.Element }[] = [
  { mode: 'pen', label: 'Pen', icon: IconPen },
  { mode: 'highlighter', label: 'Highlighter', icon: IconMarker },
  { mode: 'eraser', label: 'Eraser', icon: IconEraser },
];

/** Options strip next to the toolbar for tools that have settings (shape, draw). */
export function ToolOptions() {
  const tool = usePane((s) => s.tool);
  if (tool === 'shape') return <ShapeOptions />;
  if (tool === 'draw') return <DrawOptions />;
  return null;
}

function ShapeOptions() {
  const s = useToolSettings((t) => t);
  return (
    <div className="tool-options" onPointerDown={(e) => e.stopPropagation()}>
      <div className="opt-row">
        {SHAPES.map(({ kind, label, icon: Icon }) => (
          <button
            key={kind}
            type="button"
            className={`tb-btn${s.shapeKind === kind ? ' is-active' : ''}`}
            title={label}
            aria-label={label}
            onClick={() => setToolSettings({ shapeKind: kind })}
          >
            <Icon />
          </button>
        ))}
      </div>
      <div className="opt-label">Fill</div>
      <ColorPicker value={s.fill} onChange={(fill) => setToolSettings({ fill })} allowNone label="Fill" />
      <div className="opt-label">Stroke</div>
      <ColorPicker value={s.stroke} onChange={(stroke) => setToolSettings({ stroke })} allowNone label="Stroke" />
      <SizePicker value={s.strokeWidth} options={[1, 2, 3, 6, 10]} onChange={(strokeWidth) => setToolSettings({ strokeWidth })} />
    </div>
  );
}

function DrawOptions() {
  const s = useToolSettings((t) => t);
  return (
    <div className="tool-options" onPointerDown={(e) => e.stopPropagation()}>
      <div className="opt-row">
        {PENS.map(({ mode, label, icon: Icon }) => (
          <button
            key={mode}
            type="button"
            className={`tb-btn${s.penMode === mode ? ' is-active' : ''}`}
            title={label}
            aria-label={label}
            onClick={() => setToolSettings({ penMode: mode })}
          >
            <Icon />
          </button>
        ))}
      </div>
      {s.penMode === 'pen' && (
        <>
          <ColorPicker value={s.penColor} onChange={(penColor) => setToolSettings({ penColor })} label="Pen colour" />
          <SizePicker value={s.penSize} options={[2, 4, 8, 14, 24]} onChange={(penSize) => setToolSettings({ penSize })} />
        </>
      )}
      {s.penMode === 'highlighter' && (
        <>
          <ColorPicker
            value={s.highlighterColor}
            onChange={(highlighterColor) => setToolSettings({ highlighterColor })}
            label="Highlighter colour"
          />
          <SizePicker
            value={s.highlighterSize}
            options={[12, 24, 40]}
            onChange={(highlighterSize) => setToolSettings({ highlighterSize })}
          />
        </>
      )}
      {s.penMode === 'eraser' && (
        <SizePicker value={s.eraserSize} options={[12, 24, 48]} onChange={(eraserSize) => setToolSettings({ eraserSize })} />
      )}
      <label className="opt-check" title="A single finger never draws or moves items; two fingers still pan and zoom">
        <input type="checkbox" checked={s.penOnly} onChange={(e) => setToolSettings({ penOnly: e.target.checked })} />
        Pen only (ignore touch)
      </label>
    </div>
  );
}
